import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Logo, Monogram } from "@/components/Logo";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/admin/dashboard", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-cream/25 focus:border-terracotta outline-none py-3 font-sans text-cream placeholder:text-cream/40 transition-colors duration-300";

  return (
    <div className="min-h-screen bg-charcoal flex items-center justify-center px-6 relative overflow-hidden">
      <Monogram stroke="#f4e7d7" className="pointer-events-none absolute -left-20 -bottom-20 w-[400px] opacity-[0.04]" />
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-md relative z-10"
        data-testid="admin-login"
      >
        <div className="mb-10">
          <Logo color="#f4e7d7" sub="#a4946c" />
        </div>
        <h1 className="font-serif text-cream text-4xl mb-2">Admin <span className="italic text-terracotta">access</span></h1>
        <p className="font-sans text-cream/50 text-sm mb-10">Manage submissions & gallery.</p>
        <form onSubmit={submit} className="space-y-7">
          <input data-testid="login-email" type="email" className={inputCls} placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
          <input data-testid="login-password" type="password" className={inputCls} placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} />
          {error && <p data-testid="login-error" className="text-terracotta font-sans text-sm">{error}</p>}
          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit"
            className="w-full bg-terracotta text-cream py-4 font-sans uppercase tracking-[0.2em] text-xs hover:bg-cream hover:text-charcoal transition-colors duration-300 disabled:opacity-60"
          >
            {loading ? "Signing in…" : "Sign in"}
          </button>
        </form>
        <button
          onClick={() => navigate("/")}
          className="mt-8 font-sans uppercase tracking-[0.2em] text-[10px] text-cream/40 hover:text-cream transition-colors"
        >
          ← Back to site
        </button>
      </motion.div>
    </div>
  );
}
